import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { resolveImage, formatApiError, formatRM } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Edit, Trash2, X, Upload, Loader2 } from "lucide-react";

function MasterForm({ initial, onCancel, onSaved }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name_zh: initial?.name_zh || "",
    name_en: initial?.name_en || "",
    bio_zh: initial?.bio_zh || "",
    bio_en: initial?.bio_en || "",
    photo: initial?.photo || null,
    price: initial?.price ?? "",
    active: initial?.active ?? true,
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const inputCls =
    "w-full px-4 py-2.5 rounded-md border border-[#e5ebeb] focus:border-[#00868e] outline-none bg-white transition-colors duration-200";

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload/master-photo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, photo: data.path }));
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const submit = async (e) => {
    e.preventDefault();
    const priceNum = Number(form.price);
    if (!priceNum || priceNum <= 0) {
      toast.error("价格 / Price must be greater than 0");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, price: priceNum };
      if (initial?.id) await api.patch(`/admin/masters/${initial.id}`, payload);
      else await api.post("/admin/masters", payload);
      toast.success(t("common.saved"));
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="rounded-lg border border-[#e5ebeb] bg-white p-6 space-y-4" data-testid="master-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="text-xs text-[#4a6e77]">名称 (中)</label>
          <input required className={inputCls} value={form.name_zh} onChange={(e) => setForm({ ...form, name_zh: e.target.value })} data-testid="mf-name-zh" />
        </div>
        <div>
          <label className="text-xs text-[#4a6e77]">Name (EN)</label>
          <input required className={inputCls} value={form.name_en} onChange={(e) => setForm({ ...form, name_en: e.target.value })} data-testid="mf-name-en" />
        </div>
        <div>
          <label className="text-xs text-[#4a6e77]">介绍 (中)</label>
          <textarea rows={3} className={inputCls} value={form.bio_zh} onChange={(e) => setForm({ ...form, bio_zh: e.target.value })} data-testid="mf-bio-zh" />
        </div>
        <div>
          <label className="text-xs text-[#4a6e77]">Bio (EN)</label>
          <textarea rows={3} className={inputCls} value={form.bio_en} onChange={(e) => setForm({ ...form, bio_en: e.target.value })} data-testid="mf-bio-en" />
        </div>
      </div>

      <div>
        <div className="text-xs text-[#4a6e77] mb-2">{t("masters.photo")}</div>
        <div className="flex items-start gap-4">
          {form.photo && (
            <div className="relative h-32 w-32 rounded overflow-hidden border border-[#e5ebeb]">
              <img src={resolveImage(form.photo)} alt="" className="h-full w-full object-cover" />
              <button type="button" onClick={() => setForm({ ...form, photo: null })} className="absolute top-1 right-1 h-6 w-6 rounded-full bg-white text-red-600 text-xs flex items-center justify-center">
                <X size={12} />
              </button>
            </div>
          )}
          <label className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-[#e5ebeb] text-sm text-[#003645] hover:border-[#00868e] cursor-pointer" data-testid="mf-photo-upload-label">
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {t("admin.uploadImage")}
            <input type="file" accept="image/*" className="hidden" onChange={uploadPhoto} disabled={uploading} data-testid="mf-photo-upload-input" />
          </label>
        </div>
      </div>

      <div>
        <label className="text-xs text-[#4a6e77]">预约价格 / Booking Price (RM)</label>
        <input
          type="number"
          step="0.01"
          min="0.01"
          required
          className={inputCls + " max-w-[200px]"}
          value={form.price}
          onChange={(e) => setForm({ ...form, price: e.target.value })}
          data-testid="mf-price"
        />
      </div>

      <label className="flex items-center gap-2 text-sm text-[#003645]">
        <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="mf-active" />
        {t("masters.active")}
      </label>

      <div className="flex gap-3 pt-2">
        <button type="submit" disabled={saving} className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-[#005870] text-white font-medium hover:bg-[#003645] disabled:opacity-60" data-testid="mf-save-btn">
          {saving && <Loader2 size={14} className="animate-spin" />}{t("common.save")}
        </button>
        <button type="button" onClick={onCancel} className="px-6 py-2.5 rounded-full border border-[#e5ebeb] text-[#003645] hover:bg-[#e5ebeb]/40">
          {t("common.cancel")}
        </button>
      </div>
    </form>
  );
}

export default function AdminMasters() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/masters", { params: { include_inactive: true } }).then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this master?")) return;
    try { await api.delete(`/admin/masters/${id}`); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="p-6 md:p-10" data-testid="admin-masters-page">
      <div className="flex items-center justify-between">
        <h1 className="font-serif-cn text-3xl text-[#003645] font-semibold">{t("masters.admin")}</h1>
        {!editing && (
          <button
            onClick={() => setEditing({})}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#005870] text-white text-sm font-medium hover:bg-[#003645]"
            data-testid="new-master-btn"
          >
            <Plus size={14} /> {t("masters.newMaster")}
          </button>
        )}
      </div>

      {editing ? (
        <div className="mt-6">
          <MasterForm initial={editing.id ? editing : null} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
        </div>
      ) : (
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-masters-list">
          {items.map((m) => (
            <div key={m.id} className="rounded-lg border border-[#e5ebeb] bg-white p-4 flex gap-3" data-testid={`master-card-${m.id}`}>
              <div className="h-20 w-20 rounded-md overflow-hidden bg-[#f5f0e8] shrink-0">
                {m.photo && <img src={resolveImage(m.photo)} alt="" className="h-full w-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-[#003645] truncate">{m.name_zh}</div>
                <div className="text-xs text-[#4a6e77] truncate">{m.name_en}</div>
                <div className="mt-1 text-sm text-[#005870] font-semibold" data-testid={`master-card-price-${m.id}`}>{formatRM(m.price || 0)}</div>
                <div className="mt-2 flex items-center gap-2">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${m.active ? "bg-[#00868e]/10 text-[#00868e]" : "bg-red-50 text-red-700"}`}>
                    {m.active ? t("common.active") : t("common.inactive")}
                  </span>
                </div>
                <div className="mt-3 flex gap-3 text-xs">
                  <button onClick={() => setEditing(m)} className="text-[#005870] hover:text-[#00868e]" data-testid={`edit-master-${m.id}`}><Edit size={12} className="inline" /> {t("common.edit")}</button>
                  <button onClick={() => del(m.id)} className="text-red-600 hover:text-red-700" data-testid={`delete-master-${m.id}`}><Trash2 size={12} className="inline" /> {t("common.delete")}</button>
                </div>
              </div>
            </div>
          ))}
          {items.length === 0 && <div className="text-[#4a6e77]">{t("common.empty")}</div>}
        </div>
      )}
    </div>
  );
}
